import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Crosshair, UserCheck, DoorOpen, ShoppingBag, Flame, AlertTriangle, Eye, Scale, FileCheck, Radio, Award } from 'lucide-react';

const armedGuard = "https://images.unsplash.com/photo-1618371690240-e0d46eead4b8?w=800";

const guardServices = [
  {
    icon: <Crosshair className="h-6 w-6" />,
    title: 'Armed Officer Services',
    description: 'Our armed security officers hold valid California Guard Cards and Exposed Firearm Permits with regular range qualifications. They are trained in use-of-force protocols, threat assessment, and high-stress decision-making. Armed officers are deployed for high-risk sites, valuable asset protection, and locations requiring maximum deterrence.',
    ideal: 'Ideal for high-value properties, cannabis facilities, and financial institutions.'
  },
  {
    icon: <UserCheck className="h-6 w-6" />,
    title: 'Unarmed Security Officers',
    description: 'Professional uniformed or plainclothes officers providing visible deterrence, access control, and customer-facing security. Our unarmed guards are trained in observation, de-escalation, conflict resolution, and emergency procedures. They serve as the first point of contact for visitors while maintaining a secure environment.',
    ideal: 'Perfect for retail, office buildings, residential communities, and events.'
  },
  {
    icon: <DoorOpen className="h-6 w-6" />,
    title: 'Access Control & Gate Management',
    description: 'Controlled entry and exit point management with visitor logging, credential verification, vehicle inspections, and package screening. Our officers enforce access policies consistently while maintaining a professional, welcoming presence for authorized personnel and guests.',
    ideal: 'Essential for gated communities, corporate campuses, and industrial facilities.'
  },
  {
    icon: <ShoppingBag className="h-6 w-6" />,
    title: 'Loss Prevention & Retail Security',
    description: 'Trained loss prevention officers who reduce shrinkage through visible deterrence, undercover surveillance, shoplifter apprehension, and employee theft prevention. We work with your management to develop store-specific protocols that protect inventory without disrupting the customer experience.',
    ideal: 'Ideal for retail stores, shopping centers, and high-theft locations.'
  },
  {
    icon: <Flame className="h-6 w-6" />,
    title: 'Fire Watch Services',
    description: 'When fire alarm or suppression systems are down, California law requires a trained fire watch officer on-site. Our fire watch guards conduct continuous patrols, monitor for fire hazards, maintain communication with the fire department, and document all observations per NFPA and local fire marshal requirements.',
    ideal: 'Required during construction, system maintenance, and equipment failures.'
  },
  {
    icon: <AlertTriangle className="h-6 w-6" />,
    title: 'Workplace Violence Prevention',
    description: 'Security officers trained in recognizing pre-incident indicators, managing disgruntled individuals, conducting safe employee terminations, and maintaining a secure work environment. We coordinate with HR and management to develop workplace threat response plans and provide security escorts when needed.',
    ideal: 'Critical for corporate offices, healthcare facilities, and government buildings.'
  },
  {
    icon: <Eye className="h-6 w-6" />,
    title: 'Standing Post & Roving Patrol',
    description: 'Flexible deployment options including fixed post officers at lobbies, entrances, and critical areas, combined with roving patrols that cover parking structures, common areas, and building perimeters. Randomized patrol patterns ensure unpredictability and maximum coverage.',
    ideal: 'Suitable for any facility requiring continuous on-site security presence.'
  },
  {
    icon: <Scale className="h-6 w-6" />,
    title: 'Use of Force Continuum',
    description: 'Every officer is trained on a structured use-of-force continuum — from verbal commands and presence through physical control techniques. All actions are proportional, lawful, and thoroughly documented. Our escalation protocols are designed to resolve situations with the minimum force necessary.',
    ideal: 'Applied across all security operations for legal compliance and safety.'
  }
];

const officerStandards = [
  { icon: <Shield className="h-5 w-5" />, text: 'California BSIS Guard Card Required' },
  { icon: <Crosshair className="h-5 w-5" />, text: 'Exposed Firearm Permit (Armed Officers)' },
  { icon: <FileCheck className="h-5 w-5" />, text: 'Thorough Background Check & Drug Screening' },
  { icon: <Award className="h-5 w-5" />, text: 'Ongoing Training & Performance Reviews' },
  { icon: <Radio className="h-5 w-5" />, text: 'Radio-Equipped with Dispatch Access' },
  { icon: <CheckCircle className="h-5 w-5" />, text: 'CPR/First Aid & AED Certified' }
];

export const ArmedUnarmedSection = () => {
  return (
    <div id="armed-security" className="py-20 bg-[#121212]" data-testid="service-section-armed-security">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="lg:order-2">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-2">Professional Guard Services</p>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
              Armed & Unarmed Security.<br /><span className="text-[#F5C518]">Trained. Certified. Ready.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Our professional security officers are the backbone of our operation. Whether you need armed protection for high-risk environments or professional unarmed officers for access control and customer-facing security, every officer we deploy is background-verified, continuously trained, and held to the highest standards of professionalism and accountability.
            </p>
            <div className="bg-[#0A0A0A] border-l-4 border-[#F5C518] p-4 mb-6">
              <p className="text-white font-semibold">Not just a warm body in a uniform.</p>
              <p className="text-[#F5C518]">Vetted, trained, and mission-ready professionals.</p>
            </div>
            <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#E5B508] transition-all duration-300" data-testid="service-cta-armed-security">
              <span>Request Guard Services</span>
            </Link>
          </div>
          <div className="lg:order-1">
            <div className="relative">
              <img src={armedGuard} alt="Professional Security Officer" className="w-full aspect-[4/3] object-cover border border-zinc-800" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/50 to-transparent" />
            </div>
          </div>
        </div>

        {/* Service Capabilities */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed'] text-center">
            Guard <span className="text-[#F5C518]">Services</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {guardServices.map((service) => (
              <div key={service.title} className="bg-[#0A0A0A] border border-zinc-800 p-6 hover:border-[#F5C518]/50 transition-colors">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5C518]/10 text-[#F5C518] mb-4">
                  {service.icon}
                </div>
                <h4 className="text-lg font-bold text-white mb-3 font-['Barlow_Condensed'] uppercase">{service.title}</h4>
                <p className="text-zinc-400 text-sm mb-3">{service.description}</p>
                <p className="text-[#F5C518] text-xs font-semibold">{service.ideal}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Officer Standards & Industries */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-[#0A0A0A] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Officer <span className="text-[#F5C518]">Standards</span>
            </h3>
            <div className="space-y-4">
              {officerStandards.map((item) => (
                <div key={item.text} className="flex items-center text-zinc-400">
                  <span className="text-[#F5C518] mr-3">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-6 pt-6 border-t border-zinc-800">
              We maintain zero tolerance for negligence. Every officer represents our reputation and your safety.
            </p>
          </div>

          <div className="bg-[#0A0A0A] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Industries We <span className="text-[#F5C518]">Serve</span>
            </h3>
            <p className="text-zinc-400 mb-4">Our guard services protect clients across every sector:</p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Commercial office buildings and corporate campuses
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Retail stores and shopping centers
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Healthcare facilities and hospitals
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Residential communities and HOAs
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Construction sites and industrial facilities
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Cannabis dispensaries and cultivation sites
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Hotels, resorts, and hospitality venues
              </li>
            </ul>
            <p className="text-[#F5C518] text-sm font-semibold">
              Custom staffing solutions for any industry or requirement.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#0A0A0A] border border-[#F5C518]/30 p-8 text-center">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
            Staff Your Property with <span className="text-[#F5C518]">Professionals</span>
          </h3>
          <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
            Tell us about your facility and security needs. We will recommend the right officer type, staffing level, and coverage schedule for your budget.
          </p>
          <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300">
            <span>Request Guard Services</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
